import { EmissionFragment, EmissionType } from '../../generated/graphql'
import { reduceToSum } from '../../util/array-utility'
import { EmissionModel } from '../../emission-coefficients/emission-coefficients'
import { calculateTotalCarbonFootprint } from './carbon-footprint-calculator'
import { CalculatedVersion } from '../use-calculator'

export type Emission = Pick<EmissionFragment, 'emissionType' | 'value'>

const createMapEmissionByEmissionRules =
  (currentYearIndex: number, lastYearIndex: number) =>
  ({ emissionType, value }: Emission) => {
    switch (emissionType) {
      case EmissionType.Initial:
      case EmissionType.Maintenance: {
        if (currentYearIndex === 0) {
          return value
        }
        return 0
      }
      case EmissionType.Recurring: {
        if (currentYearIndex !== 0) {
          return value
        }
        return 0
      }
      case EmissionType.Disassembly: {
        if (currentYearIndex === lastYearIndex) {
          return value
        }
        return 0
      }
      case EmissionType.Handprint: {
        if (currentYearIndex === 0) {
          return -value
        }
        return 0
      }
      default: {
        throw new Error(
          `Emission calculation not implemented for type ${emissionType}`,
        )
      }
    }
  }

export function calculateTotalFixedCarbonFootprint(
  emissions: Emission[],
  emissionModel: EmissionModel,
): number {
  const mapYearIndexToValue = (yearIndex: number) => {
    const mapEmissionByEmissionRules = createMapEmissionByEmissionRules(
      yearIndex, // an index from 0 - inspectionLengthInYears
      emissionModel.inspectionLengthInYears - 1, // -1 to get the last year index, e.g. 0-2 = 3 years
    )
    return emissions.map(mapEmissionByEmissionRules).reduce(reduceToSum, 0)
  }

  return calculateTotalCarbonFootprint(mapYearIndexToValue, emissionModel)
}

export function calculateInherentFixedCarbonFootprint(
  emissions: Emission[],
  emissionModel: EmissionModel,
  area: number,
): number {
  const totalFootprint = calculateTotalFixedCarbonFootprint(
    emissions,
    emissionModel,
  )

  return totalFootprint / (area * emissionModel.inspectionLengthInYears)
}

export function getFixedResults(
  versions: CalculatedVersion[],
  calculateResult: (
    emissions: Emission[],
    model: EmissionModel,
    area: number,
  ) => number,
  emissionModel: EmissionModel,
  emissionType?: EmissionType,
  selectedBuildingIds?: string[],
): (number | null)[] {
  return versions.map((version) => {
    const versionHasSelectedBuilding =
      selectedBuildingIds &&
      selectedBuildingIds.some(
        (buildingId) => version.buildingIdsToAreas[buildingId] !== undefined,
      )

    if (selectedBuildingIds && !versionHasSelectedBuilding) {
      return null
    }

    const filteredEmissions = version.emissions
      .filter((emission) =>
        emissionType ? emission.emissionType === emissionType : true,
      )
      .filter((emission) =>
        selectedBuildingIds
          ? selectedBuildingIds.includes(emission.buildingId)
          : true,
      )

    const area = selectedBuildingIds
      ? selectedBuildingIds
          .map((buildingId) => version.buildingIdsToAreas[buildingId] ?? 0)
          .reduce(reduceToSum, 0)
      : version.totalArea

    return calculateResult(filteredEmissions, emissionModel, area)
  })
}
